import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Nav from "@/components/Nav";
import ReactionModal from "@/components/ReactionModal";
import StarRating from "@/components/StarRating";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Swords, Heart, ArrowLeft, Download, Lock, Skull, Sparkles, Feather, MessageCircle, Star, FileText } from "lucide-react";

const TYPE_LABEL = {
  rpg: "Tabletop RPG",
  novel: "Novel",
  screenplay: "Screenplay",
  video_game: "Video Game",
  comic: "Comic",
  other: "Other",
};

const STAT_KEYS = ["str", "dex", "con", "int", "wis", "cha"];

export default function CharacterDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [char, setChar] = useState(null);
  const [error, setError] = useState(null);
  const [buying, setBuying] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const [wall, setWall] = useState([]);
  const [reactionOpen, setReactionOpen] = useState(false);

  const load = () => {
    api.get(`/characters/${id}`)
      .then(({ data }) => setChar(data))
      .catch(() => setError(true));
  };
  useEffect(load, [id]);

  useEffect(() => {
    if (char && (char.is_author || char.has_purchased)) {
      api.get(`/characters/${id}/reactions/wall`)
        .then(({ data }) => setWall(data))
        .catch((err) => { console.error("Character wall load failed", err); setWall([]); });
    }
  }, [char, id]);

  const starReaction = async (reactionId) => {
    try {
      const { data } = await api.post(`/character-reactions/${reactionId}/star`);
      setWall((prev) => prev.map((r) => r.id === reactionId ? { ...r, starred: data.starred } : r));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const buy = async () => {
    if (!user || user === false) { toast.info("Sign in to unlock this character."); return; }
    setBuying(true);
    try {
      const { data } = await api.post("/characters/checkout", { character_id: id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBuying(false);
    }
  };

  const exportJson = async () => {
    await downloadFile(`/characters/${id}/export.json`, `${char.name.replace(/\s+/g, "_")}.json`);
  };
  const exportPdf = async () => {
    await downloadFile(`/characters/${id}/export.pdf`, `${char.name.replace(/\s+/g, "_")}.pdf`);
  };

  const downloadFile = async (path, filename) => {
    try {
      const token = localStorage.getItem("quill_token");
      const resp = await fetch(`${API}${path}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!resp.ok) throw new Error(await resp.text());
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed.");
    }
  };

  if (error) return (
    <div className="min-h-screen grain">
      <Nav />
      <div className="max-w-3xl mx-auto py-24 text-center">
        <h1 className="font-serif-display text-4xl text-[#F7F3EC] mb-3">This character has wandered off.</h1>
        <Link to="/vault" className="text-[#D9B472]">Back to the Vault →</Link>
      </div>
    </div>
  );
  if (!char) return <div className="min-h-screen grain"><Nav /></div>;

  const canRead = char.is_author || char.has_purchased;
  const gallery = char.gallery_paths || [];
  const activeSrc = gallery[activeImage] ? `${API}/files/${gallery[activeImage]}` : null;
  const price = ((char.price_cents || 0) / 100).toFixed(2);

  return (
    <div className="min-h-screen grain">
      <Nav />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 sm:py-12">
        <Link to="/vault" className="inline-flex items-center gap-2 text-[#6E675E] hover:text-[#D9B472] font-mono-brass text-xs uppercase tracking-widest mb-6 transition-colors duration-200" data-testid="detail-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to the Vault
        </Link>

        <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-8 lg:gap-14">
          {/* Gallery */}
          <div>
            <div className="gold-frame paper-scroll rounded-sm overflow-hidden aspect-[4/5] relative bg-gradient-to-br from-[#2b2620] to-[#0D0C0A]" data-testid="detail-gallery">
              {activeSrc ? (
                <img src={activeSrc} alt={char.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  {char.character_type === "rpg" ? <Swords className="w-20 h-20 text-[#D9B472] opacity-40" /> : <Skull className="w-20 h-20 text-[#D9B472] opacity-40" />}
                </div>
              )}
              {char.license_tier === "roleplay" && (
                <div className="absolute top-3 left-3 chip bg-[#8C3A34]/85 backdrop-blur text-[#F7E5DE] border-[rgba(233,184,168,0.3)]">
                  <Heart className="w-3 h-3 inline mr-1" /> Roleplay license included
                </div>
              )}
            </div>
            {gallery.length > 1 && (
              <div className="mt-3 flex gap-2 flex-wrap">
                {gallery.map((g, i) => (
                  <button
                    key={g}
                    onClick={() => setActiveImage(i)}
                    className={`w-16 h-16 rounded-sm overflow-hidden border ${i === activeImage ? "border-[#D9B472]" : "border-[rgba(217,180,114,0.15)]"}`}
                    data-testid={`detail-thumb-${i}`}
                  >
                    <img src={`${API}/files/${g}`} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Header + hook + buy */}
          <div>
            <div className="chip mb-3">{TYPE_LABEL[char.character_type] || char.character_type}</div>
            <h1 className="font-serif-display text-4xl sm:text-6xl italic text-[#F7F3EC] tracking-tight mb-3" data-testid="detail-name">
              {char.name}
            </h1>
            <p className="font-serif-body text-lg text-[#A8A095] italic mb-2">{char.hook}</p>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass mb-6">
              <Feather className="w-3 h-3 text-[#D9B472]" /> Made by <Link to={`/author/${encodeURIComponent(char.author_pseudonym)}`} className="text-[#D9B472] hover:underline">{char.author_pseudonym}</Link>
            </div>

            <div className="mb-6" data-testid="character-rating-header">
              <StarRating
                avg={char.avg_rating || 0}
                count={char.rating_count || 0}
                kind="character"
                id={char.id}
                canRate={char.has_purchased && !char.is_author}
                size="lg"
                onRated={(agg) => setChar((c) => ({ ...c, avg_rating: agg.avg_rating, rating_count: agg.rating_count }))}
              />
            </div>

            {char.tags?.length > 0 && (
              <div className="mb-6 flex flex-wrap gap-2">
                {char.tags.map((t) => (
                  <span key={t} className="text-[10px] font-mono-brass px-2.5 py-1 border border-[rgba(217,180,114,0.2)] text-[#A8A095] rounded-sm">{t}</span>
                ))}
              </div>
            )}

            {char.aliases?.length > 0 && (
              <div className="mb-6">
                <div className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass mb-1">Also known as</div>
                <div className="font-serif-display italic text-lg text-[#F7F3EC]">
                  {char.aliases.join(" · ")}
                </div>
              </div>
            )}

            <div className="gold-frame paper-scroll rounded-sm p-5 mb-6">
              <div className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass mb-2">Personality</div>
              <p className="font-serif-body text-[#F7F3EC] leading-relaxed">{char.personality}</p>
            </div>

            {canRead ? (
              <div className="flex flex-wrap gap-3 mb-6">
                <button onClick={exportJson} className="btn-buy px-5 py-3 rounded-sm font-mono-brass text-xs uppercase tracking-widest font-semibold inline-flex items-center gap-2" data-testid="detail-export">
                  <Download className="w-3.5 h-3.5" /> Export JSON
                </button>
                <button onClick={exportPdf} className="btn-ghost-gold px-5 py-3 rounded-sm font-mono-brass text-xs uppercase tracking-widest inline-flex items-center gap-2" data-testid="detail-export-pdf">
                  <FileText className="w-3.5 h-3.5" /> Print-ready PDF
                </button>
                {char.has_purchased && !char.is_author && (
                  <button onClick={() => setReactionOpen(true)} className="btn-ghost-gold px-5 py-3 rounded-sm font-mono-brass text-xs uppercase tracking-widest inline-flex items-center gap-2" data-testid="detail-leave-reaction">
                    <Heart className="w-3.5 h-3.5" /> Leave a note
                  </button>
                )}
                {char.is_author && (
                  <span className="text-[10px] uppercase tracking-widest text-[#D9B472] font-mono-brass self-center">Yours · listed for ${price}</span>
                )}
              </div>
            ) : (
              <div className="border border-[rgba(217,180,114,0.2)] rounded-sm p-5 mb-6 bg-[rgba(217,180,114,0.03)]">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-[#E6B860]" />
                  <span className="text-[10px] uppercase tracking-widest text-[#E6B860] font-mono-brass">Unlock the full dossier</span>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="font-serif-display text-2xl text-[#F7F3EC]">${price}</div>
                    <div className="text-xs text-[#A8A095] font-serif-body italic">One payment. Backstory, full build, JSON export. {char.license_tier === "roleplay" && "Includes roleplay licence."}</div>
                  </div>
                  <button onClick={buy} disabled={buying} className="btn-buy px-6 py-3 rounded-sm font-mono-brass text-xs uppercase tracking-widest font-semibold disabled:opacity-50" data-testid="detail-buy">
                    {buying ? "Redirecting…" : "Unlock character"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Preview backstory (public) */}
        <section className="mt-16 max-w-3xl">
          <h2 className="font-serif-display text-2xl text-[#F7F3EC] mb-4">The tale so far</h2>
          <div className="paper-scroll gold-frame rounded-sm p-6 sm:p-8">
            <p className="font-serif-body text-[#F7F3EC] text-lg leading-relaxed whitespace-pre-wrap">{char.preview_backstory}</p>
          </div>
        </section>

        {/* Locked full backstory */}
        <section className="mt-10 max-w-3xl">
          <h2 className="font-serif-display text-2xl text-[#F7F3EC] mb-4 flex items-center gap-2">
            {canRead ? <Sparkles className="w-5 h-5 text-[#E6B860]" /> : <Lock className="w-5 h-5 text-[#6E675E]" />}
            Full backstory
          </h2>
          {canRead ? (
            <div className="paper-scroll gold-frame rounded-sm p-6 sm:p-8" data-testid="detail-full-backstory">
              <p className="font-serif-body text-[#F7F3EC] text-lg leading-relaxed whitespace-pre-wrap">{char.full_backstory}</p>
            </div>
          ) : (
            <div className="border border-dashed border-[rgba(217,180,114,0.2)] rounded-sm p-6 sm:p-8 relative overflow-hidden select-none pointer-events-none" data-testid="detail-full-locked">
              <div className="blur-sm opacity-40 font-serif-body text-[#F7F3EC] text-base leading-relaxed">
                {"…the war had already forgotten them by the second campaign, but the enemy hadn't. Every silent night in the barrows, they came back three at a time — first the horseman, then the lover, then the one whose name we still don't speak. ".repeat(3)}
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="chip"><Lock className="w-3 h-3" /> Unlock to read</div>
              </div>
            </div>
          )}
        </section>

        {/* Build */}
        <section className="mt-10 max-w-3xl">
          <h2 className="font-serif-display text-2xl text-[#F7F3EC] mb-4 flex items-center gap-2">
            {canRead ? <Swords className="w-5 h-5 text-[#D9B472]" /> : <Lock className="w-5 h-5 text-[#6E675E]" />}
            {char.has_rpg_build ? "Stat block & build" : "Character build"}
          </h2>
          {canRead ? (
            <div className="gold-frame paper-scroll rounded-sm p-6 sm:p-8" data-testid="detail-build">
              {char.has_rpg_build && char.rpg_build && <StatBlock build={char.rpg_build} />}
              {char.freeform_build && (
                <div className={char.has_rpg_build ? "mt-6 pt-6 border-t border-[rgba(217,180,114,0.15)]" : ""}>
                  <div className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass mb-3">Build notes</div>
                  <p className="font-serif-body text-[#F7F3EC] leading-relaxed whitespace-pre-wrap">{char.freeform_build}</p>
                </div>
              )}
              {!char.has_rpg_build && !char.freeform_build && (
                <p className="text-[#6E675E] font-serif-body italic">Author left the build free-form — nothing on file.</p>
              )}
            </div>
          ) : (
            <div className="border border-dashed border-[rgba(217,180,114,0.2)] rounded-sm p-6 sm:p-8 relative overflow-hidden select-none pointer-events-none">
              <div className="blur-sm opacity-40 grid grid-cols-3 sm:grid-cols-6 gap-3">
                {STAT_KEYS.map((k) => (
                  <div key={k} className="border border-[rgba(217,180,114,0.15)] rounded-sm p-2 text-center">
                    <div className="text-[10px] uppercase text-[#6E675E]">{k}</div>
                    <div className="font-mono-brass text-2xl text-[#F7F3EC]">??</div>
                  </div>
                ))}
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="chip"><Lock className="w-3 h-3" /> Unlock to see stats & gear</div>
              </div>
            </div>
          )}
        </section>
        {/* Wall — only buyers + author see it */}
        {canRead && (
          <section className="mt-10 max-w-3xl" data-testid="character-wall-section">
            <h2 className="font-serif-display text-2xl text-[#F7F3EC] mb-4 flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-[#D9B472]" /> Reader notes
              {wall.length > 0 && <span className="text-xs text-[#6E675E] font-mono-brass">· {wall.length}</span>}
            </h2>
            {wall.length === 0 ? (
              <div className="paper-scroll gold-frame rounded-sm p-6 text-center">
                <p className="text-[#6E675E] font-serif-body italic mb-2">
                  {char.is_author ? "No reader notes yet. Once buyers unlock this character they can leave you one line each." : `Be the first to leave a note for ${char.author_pseudonym}.`}
                </p>
                {char.has_purchased && !char.is_author && (
                  <button onClick={() => setReactionOpen(true)} className="btn-ghost-gold px-4 py-2 rounded-sm font-mono-brass text-[10px] uppercase tracking-widest inline-flex items-center gap-2 mt-2" data-testid="character-wall-empty-cta">
                    <Heart className="w-3 h-3" /> Leave a note
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-3" data-testid="character-wall">
                {wall.map((r) => (
                  <blockquote key={r.id} className={`paper-scroll gold-frame rounded-sm p-5 ${r.starred ? "ring-1 ring-[#D9B472]" : ""}`} data-testid={`character-wall-${r.id}`}>
                    <p className="font-serif-body italic text-lg text-[#F7F3EC] leading-relaxed">"{r.line}"</p>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-[rgba(217,180,114,0.1)]">
                      <span className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass">Anonymous reader · {new Date(r.created_at).toLocaleDateString()}</span>
                      {char.is_author && (
                        <button onClick={() => starReaction(r.id)} className={`text-[10px] uppercase tracking-widest font-mono-brass flex items-center gap-1 transition-colors duration-200 ${r.starred ? "text-[#E6B860]" : "text-[#6E675E] hover:text-[#D9B472]"}`} data-testid={`character-star-${r.id}`}>
                          <Star className={`w-3 h-3 ${r.starred ? "fill-[#E6B860]" : ""}`} /> {r.starred ? "Starred" : "Star"}
                        </button>
                      )}
                    </div>
                  </blockquote>
                ))}
              </div>
            )}
          </section>
        )}
      </div>

      {reactionOpen && char && (
        <ReactionModal
          subjectLabel={char.name}
          submitPath={`/characters/${char.id}/reactions`}
          copy={{
            title: `Leave a note for ${char.author_pseudonym}`,
            subtitle: `One line the character ${char.name} left with you. Only the author will read it.`,
            placeholder: `What ${char.name} carried out of me…`,
          }}
          onClose={() => setReactionOpen(false)}
          onCreated={() => {
            api.get(`/characters/${id}/reactions/wall`).then(({ data }) => setWall(data)).catch(() => {});
          }}
        />
      )}
    </div>
  );
}

function StatBlock({ build }) {
  const stats = build.stats || {};
  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          ["System", build.system],
          ["Class", build.character_class],
          ["Race", build.race],
          ["Level", build.level],
          ["HP", build.hp],
          ["AC", build.ac],
        ].filter(([, v]) => v != null && v !== "").map(([k, v]) => (
          <div key={k} className="border border-[rgba(217,180,114,0.15)] rounded-sm p-3">
            <div className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass mb-1">{k}</div>
            <div className="font-mono-brass text-lg text-[#F7F3EC]">{v}</div>
          </div>
        ))}
      </div>
      {Object.keys(stats).length > 0 && (
        <div className="mb-6">
          <div className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass mb-2">Ability scores</div>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {STAT_KEYS.map((k) => (
              <div key={k} className="border border-[rgba(217,180,114,0.15)] rounded-sm p-2 text-center">
                <div className="text-[10px] uppercase text-[#D9B472] font-mono-brass">{k}</div>
                <div className="font-mono-brass text-2xl text-[#F7F3EC]">{stats[k] ?? "—"}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      {["gear", "spells", "abilities"].map((k) => (
        build[k]?.length > 0 && (
          <div key={k} className="mb-4">
            <div className="text-[10px] uppercase tracking-widest text-[#D9B472] font-mono-brass mb-2">{k}</div>
            <ul className="grid sm:grid-cols-2 gap-1">
              {build[k].map((item, i) => (
                <li key={i} className="text-[#F7F3EC] font-serif-body text-sm">· {item}</li>
              ))}
            </ul>
          </div>
        )
      ))}
    </>
  );
}
